import { Switch } from "@/components/ui/switch";
import { levelOf, type Capability, type CapabilityLevel, type Choice } from "@/lib/capability-access";
import { cn } from "@/lib/utils";
import type { KeyboardEvent, ReactNode } from "react";

// The capability picker shared by the Microsoft 365 and Google Workspace
// connection pages.

export function CapabilityList({ children }: { children: ReactNode }) {
  return <div className="divide-y rounded-lg border">{children}</div>;
}

// One capability in a picker list: icon, name, what the chosen level allows,
// a level picker when there is more than one level, and an on/off switch.
// Rows changed since the last grant say "Not applied". Read-only viewers see
// the level as text.
export function CapabilityRow({
  idPrefix,
  cap,
  icon,
  choice,
  pending,
  editable,
  onChange,
}: {
  idPrefix: string;
  cap: Capability;
  icon: ReactNode;
  choice: Choice;
  pending: boolean;
  editable: boolean;
  onChange: (next: Choice) => void;
}) {
  const level = levelOf(cap, choice);
  const switchId = `${idPrefix}-cap-${cap.id}`;
  const picker = editable && choice.on && cap.levels.length > 1 && (
    <LevelPicker
      label={`${cap.name} access`}
      levels={cap.levels}
      value={choice.level}
      onChange={(id) => onChange({ on: true, level: id })}
    />
  );
  // The level picker sits beside the switch, and drops under the text on
  // narrow screens so the switch stays put.
  return (
    <div className="flex items-center gap-3 p-4">
      <div className="flex size-9 shrink-0 items-center justify-center self-start rounded-lg bg-muted/60">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <label htmlFor={editable ? switchId : undefined} className="text-sm font-medium">
            {cap.name}
          </label>
          {pending && (
            <span className="rounded-full bg-amber-50 px-1.5 py-px text-[11px] font-medium text-amber-700 ring-1 ring-amber-200 ring-inset dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30">
              Not applied
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{choice.on ? level.summary : "Off"}</p>
        {picker && <div className="mt-2.5 sm:hidden">{picker}</div>}
      </div>
      {picker && <div className="hidden shrink-0 sm:block">{picker}</div>}
      {editable ? (
        <Switch
          id={switchId}
          checked={choice.on}
          onCheckedChange={(on) => onChange({ ...choice, on })}
        />
      ) : (
        <span className="shrink-0 text-sm text-muted-foreground">{choice.on ? level.label : "Off"}</span>
      )}
    </div>
  );
}

// Segmented radio group for a capability's access level; arrow keys move the
// selection, like a native radio group.
function LevelPicker({
  label,
  levels,
  value,
  onChange,
}: {
  label: string;
  levels: ReadonlyArray<CapabilityLevel>;
  value: string;
  onChange: (id: string) => void;
}) {
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step =
      e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const i = levels.findIndex((l) => l.id === value);
    const next = levels[(i + step + levels.length) % levels.length]!;
    onChange(next.id);
    e.currentTarget.querySelector<HTMLElement>(`[data-level="${next.id}"]`)?.focus();
  };
  return (
    <div
      role="radiogroup"
      aria-label={label}
      onKeyDown={onKeyDown}
      className="inline-flex h-8 items-center rounded-lg bg-muted p-[3px]"
    >
      {levels.map((l) => {
        const active = l.id === value;
        return (
          <button
            key={l.id}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            data-level={l.id}
            onClick={() => onChange(l.id)}
            className={cn(
              "h-full rounded-md px-2.5 text-xs font-medium outline-none transition-[color,background-color,box-shadow] focus-visible:ring-[3px] focus-visible:ring-ring/50",
              active
                ? "bg-background text-foreground shadow-sm dark:bg-input/30"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {l.label}
          </button>
        );
      })}
    </div>
  );
}
